import { useState } from "react";
import { ExternalLink, MapPin, Star, Trash2, User, UserCheck, Calendar as CalendarIcon, Tag } from "lucide-react";
import { Link } from "react-router-dom";

import { useEventQuery } from "@/hooks/useEventQuery";
import { useApproveEvent, useRejectEvent } from "@/hooks/useEventActions";
import { useSetEventPriority } from "@/hooks/useSetEventPriority";
import { DeleteEventDialog } from "@/components/events/DeleteEventDialog";
import { StatusBadge } from "@/components/events/StatusBadge";
import { StarRating } from "@/components/ui/StarRating";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { RejectDialog } from "@/components/review/RejectDialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { formatDate, formatDateTimeLong } from "@/lib/formatters";
import { getCategoryColors } from "@/lib/outlookCategoryColors";

interface CalendarEventDetailSheetProps {
  eventId: string | null;
  onClose: () => void;
}

export function CalendarEventDetailSheet({
  eventId,
  onClose,
}: CalendarEventDetailSheetProps) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: event, isLoading, isError } = useEventQuery(eventId ?? undefined);
  const priorityMutation = useSetEventPriority();
  const approveMutation = useApproveEvent();
  const rejectMutation = useRejectEvent();

  const isManualReview = event?.status === "MANUAL_REVIEW";
  const isActing =
    approveMutation.isPending ||
    rejectMutation.isPending;

  function handleApprove() {
    if (!event) return;
    approveMutation.mutate(
      { id: event.id },
      { onSuccess: onClose },
    );
  }

  function handleRejectConfirm(reason: string | undefined) {
    if (!event) return;
    rejectMutation.mutate(
      { id: event.id, reason },
      {
        onSuccess: () => {
          setRejectOpen(false);
          onClose();
        },
      },
    );
  }

  const categoryColors = getCategoryColors(event?.category?.outlookColor);

  return (
    <>
      <Sheet
        open={Boolean(eventId)}
        onOpenChange={(open) => { if (!open) onClose(); }}
      >
        <SheetContent side="right" className="flex flex-col overflow-hidden">
          {/* Header */}
          <SheetHeader>
            {isLoading ? (
              <>
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </>
            ) : isError || !event ? (
              <>
                <SheetTitle>Evento non trovato</SheetTitle>
                <SheetDescription>
                  Impossibile caricare i dettagli dell&apos;evento.
                </SheetDescription>
              </>
            ) : (
              <>
                <SheetTitle className="pr-2 leading-snug">
                  {event.title ?? event.emailSubject}
                </SheetTitle>
                <div className="flex items-center gap-2">
                  <StatusBadge status={event.status} />
                  {event.startDate && (
                    <span className="text-xs text-muted-foreground">
                      {formatDateTimeLong(event.startDate)}
                      {event.endDate && event.endDate !== event.startDate && (
                        <> — {formatDate(event.endDate)}</>
                      )}
                    </span>
                  )}
                </div>
              </>
            )}
          </SheetHeader>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {isLoading && (
              <div className="space-y-3">
                {Array.from({ length: 5 }, (_, i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            )}

            {!isLoading && !isError && event && (
              <>
                {/* Category */}
                {event.category && (
                  <div className="flex items-center gap-2">
                    <Tag className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium"
                      style={{
                        backgroundColor: categoryColors.background,
                        color: categoryColors.foreground,
                        border: `1px solid ${categoryColors.border}`,
                      }}
                    >
                      {event.category.name}
                    </span>
                  </div>
                )}

                {/* Priority */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground w-20 shrink-0">
                    Priorità
                  </span>
                  <StarRating
                    value={event.priority as 0 | 1 | 2 | 3}
                    onChange={(p) =>
                      priorityMutation.mutate({ id: event.id, priority: p })
                    }
                    disabled={priorityMutation.isPending}
                    size="sm"
                  />
                </div>

                {/* Location */}
                {event.location && (
                  <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="text-sm">{event.location}</span>
                  </div>
                )}

                {/* Organizer */}
                {event.organizer && (
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="text-sm">{event.organizer}</span>
                  </div>
                )}

                {/* Event type */}
                {event.eventType && (
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="text-sm">{event.eventType}</span>
                  </div>
                )}

                {/* Description */}
                {event.description && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">
                      Descrizione
                    </p>
                    <p className="text-sm leading-relaxed whitespace-pre-line">
                      {event.description}
                    </p>
                  </div>
                )}

                {/* Email sender */}
                <div className="border-t pt-4 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">
                    Email originale
                  </p>
                  <p className="text-sm truncate" title={event.emailFrom}>
                    {event.emailFrom}
                  </p>
                  <p className="text-xs text-muted-foreground truncate" title={event.emailSubject}>
                    {event.emailSubject}
                  </p>
                </div>

                {/* Deep link to detail page */}
                <Link
                  to={`/events/${event.id}`}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-4 hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Apri dettaglio completo
                </Link>

                {/* Attribution */}
                {(event.prioritySetBy ?? event.reviewedBy) && (
                  <div className="border-t pt-3 space-y-1.5">
                    {event.prioritySetBy && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Star className="h-3 w-3 fill-amber-400 text-amber-400 shrink-0" />
                        <span>
                          <span className="font-medium">Priorità</span> impostata da{" "}
                          <span className="font-medium">{event.prioritySetBy.name}</span>
                          {event.prioritySetAt && (
                            <> il {formatDateTimeLong(event.prioritySetAt)}</>
                          )}
                        </span>
                      </div>
                    )}
                    {event.reviewedBy && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <UserCheck className="h-3 w-3 text-green-600 shrink-0" />
                        <span>
                          <span className="font-medium">Approvato</span> da{" "}
                          <span className="font-medium">{event.reviewedBy.name}</span>
                          {event.reviewedAt && (
                            <> il {formatDateTimeLong(event.reviewedAt)}</>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer actions */}
          {event && (
            <SheetFooter className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Elimina evento"
                onClick={() => setDeleteOpen(true)}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>

              {isManualReview && (
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setRejectOpen(true)}
                    disabled={isActing}
                  >
                    Rifiuta
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleApprove}
                    disabled={isActing}
                  >
                    {approveMutation.isPending ? "Approvazione…" : "Approva"}
                  </Button>
                </div>
              )}
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

      {/* Reject confirmation dialog */}
      <RejectDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onConfirm={handleRejectConfirm}
        isPending={rejectMutation.isPending}
        eventSubject={event?.emailSubject}
      />

      {/* Delete confirmation dialog */}
      {event && (
        <DeleteEventDialog
          event={event}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          onDeleted={onClose}
        />
      )}
    </>
  );
}
